import PageHero from "../components/PageHero";
import DestinationSection from "./components/DestinationSection";
import { DESTINATIONS, COMPARISON_ROWS } from "./data";

const STATS = [
  { value: "10", label: "Signature Hill Destinations", icon: "ri-map-2-line" },
  { value: "2,240 m", label: "Highest Elevation (Ooty)", icon: "ri-arrow-up-line" },
  { value: "26–33%", label: "Est. Annual Returns", icon: "ri-line-chart-line" },
  { value: "₹54 L", label: "Entry Investment", icon: "ri-hand-coin-line" },
];

export default function Destinations() {
  return (
    <div className="w-full">
      <PageHero
        eyebrow="Destinations · Western Ghats & Nilgiris"
        title={
          <>
            The Most Loved <span className="italic text-accent-300">Hill Retreats</span> of South India
          </>
        }
        subtitle="From Ooty's Nilgiri meadows to Munnar's tea estates, Coorg's coffee hills and the wild edge of Mudumalai — ten of India's most beloved mountain escapes, each a proven canvas for premium A-frame villa investment."
      />

      {/* Stats strip */}
      <section className="w-full py-10 md:py-12 bg-background-100">
        <div className="w-full px-4 md:px-8 max-w-6xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-5">
          {STATS.map((s) => (
            <div
              key={s.label}
              className="flex flex-col sm:flex-row items-center gap-3 rounded-2xl bg-background-50 border border-background-200 p-4"
            >
              <span className="flex items-center justify-center w-11 h-11 rounded-full bg-accent-100 text-accent-700 shrink-0">
                <i className={`${s.icon} text-xl`}></i>
              </span>
              <div className="text-center sm:text-left">
                <p className="font-heading text-2xl text-foreground-950 leading-none">{s.value}</p>
                <p className="text-xs text-foreground-600 mt-1">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Intro */}
      <section className="w-full py-12 md:py-16 bg-background-50">
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.22em] uppercase text-accent-600">
            <i className="ri-tree-line"></i> Where we build
          </span>
          <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 mt-3">
            Ten Iconic Hill Destinations, One Proven Investment Thesis
          </h2>
          <p className="text-sm md:text-base text-foreground-700 mt-4 leading-relaxed">
            The Western Ghats and the Nilgiri range — a UNESCO World Heritage region — host India's
            most treasured hill stations and nature retreats. From the tea estates of Munnar and
            Coonoor, the coffee hills of Chikmagalur and Coorg, to the rainforests of Agumbe, the
            spice forests of Wayanad and the wild corridors of Mudumalai — each offers cool climates,
            breathtaking scenery and steady tourist footfall. For villa investors, that means
            dependable occupancy, premium nightly rates and land that appreciates as scenic plots
            grow scarcer. Each destination below shares its highlights, advantages, climate and the
            case for investing with Nomad Livings.
          </p>
        </div>
      </section>

      {/* Sticky jump nav */}
      <nav className="sticky top-0 z-40 w-full bg-background-50/90 backdrop-blur border-b border-background-200 py-3">
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto flex items-center gap-2 overflow-x-auto no-scrollbar">
          <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap shrink-0 mr-2">
            <i className="ri-map-pin-2-line mr-1"></i> Jump to:
          </span>
          {DESTINATIONS.map((d) => (
            <a
              key={d.id}
              href={`#${d.id}`}
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById(d.id);
                if (el) {
                  const y = el.getBoundingClientRect().top + window.scrollY - 80;
                  window.scrollTo({ top: y, behavior: "smooth" });
                }
              }}
              className="text-xs font-medium whitespace-nowrap rounded-full px-3 py-1.5 bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-colors border border-background-200 shrink-0"
            >
              {d.name}
            </a>
          ))}
        </div>
      </nav>

      {/* Destination deep-dives */}
      <div className="w-full divide-y divide-background-200">
        {DESTINATIONS.map((d, i) => (
          <DestinationSection key={d.id} dest={d} index={i} />
        ))}
      </div>

      {/* Comparison table */}
      <section className="w-full py-12 md:py-16 bg-background-100">
        <div className="w-full px-4 md:px-8 max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.22em] uppercase text-accent-600">
              <i className="ri-stack-line"></i> At a glance
            </span>
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-3">
              Comparing All Destinations
            </h2>
            <p className="text-sm text-foreground-600 mt-2">
              Pick the setting that matches your investment style and lifestyle.
            </p>
          </div>

          <div className="overflow-x-auto rounded-2xl bg-background-50 border border-background-200">
            <table className="w-full min-w-[1200px] text-left">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/70">
                  <th className="px-3 py-4 text-xs font-bold uppercase tracking-wide text-foreground-500">
                    Comparison
                  </th>
                  {DESTINATIONS.map((d) => (
                    <th key={d.id} className="px-3 py-4">
                      <span className="font-heading text-sm text-foreground-950 whitespace-nowrap">{d.name}</span>
                      <span className="block text-[11px] font-medium text-foreground-500 mt-0.5">
                        {d.state}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row, idx) => (
                  <tr key={row.label} className={idx % 2 ? "bg-background-100/40" : ""}>
                    <td className="px-3 py-3 text-sm font-semibold text-foreground-800 whitespace-nowrap">
                      {row.label}
                    </td>
                    {DESTINATIONS.map((d) => (
                      <td
                        key={d.id}
                        className="px-3 py-3 text-xs text-foreground-700 leading-snug"
                      >
                        {String(d[row.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-foreground-500 text-center mt-4">
            ROI and pricing are indicative estimates for planning purposes and vary by plot, model and
            operating strategy.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="w-full py-12 md:py-16 bg-background-50">
        <div className="w-full px-4 md:px-8 max-w-6xl mx-auto">
          <div className="rounded-3xl bg-primary-800 text-background-100 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-6 md:p-10 flex flex-col justify-center">
                <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.22em] uppercase text-accent-300">
                  <i className="ri-compass-3-line"></i> Find your destination
                </span>
                <h3 className="font-heading text-2xl md:text-3xl text-background-50 mt-3">
                  Which Hill Retreat Suits You?
                </h3>
                <p className="text-sm text-background-200/90 mt-3 leading-relaxed">
                  Chat with our team to shortlist a destination and villa model that matches your
                  budget, returns goal and lifestyle. Download the investment guide or schedule a
                  virtual site tour today.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <a
                    href="/investment-guide"
                    className="inline-flex items-center justify-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-accent-500 text-background-50 px-6 py-3 hover:bg-accent-600 transition-colors"
                  >
                    Download Investment Guide <i className="ri-download-2-line"></i>
                  </a>
                  <a
                    href="/contact"
                    className="inline-flex items-center justify-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full border border-background-50/50 text-background-50 px-6 py-3 hover:bg-background-50 hover:text-primary-800 transition-colors"
                  >
                    Schedule a Visit <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>
              <div className="relative min-h-[260px] lg:min-h-full w-full">
                <img
                  src="https://readdy.ai/api/search-image?query=Photorealistic%20panoramic%20composite%20of%20four%20South%20Indian%20hill%20destinations:%20Chikmagalur%20coffee%20plantations%2C%20Coorg%20waterfalls%2C%20Agumbe%20rainforest%20sunset%2C%20and%20Munnar%20tea%20hills%2C%20seamless%20landscape%20blend%2C%20golden%20warm%20light%2C%20real%20aerial%20photography%20style%2C%20ultra%20high%20detail&width=900&height=620&seq=dest-cta-04&orientation=landscape&nocache=true"
                  alt="South Indian hill and nature destinations for villa investment"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}